import ChevronRight from "@mui/icons-material/ChevronRight";
import { Card, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { grey } from "@mui/material/colors";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import LinearProgress from "@mui/material/LinearProgress";
import MobileStepper from "@mui/material/MobileStepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import arrayMutators from "final-form-arrays";
import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-final-form";
import isNil from "lodash/isNil";
import { camelCaseToSentence } from "../../utils/strings/camelCaseToSentence";
import EnvLogo from "../env/EnvLogo";
import AnimatedCheckmark from "../feedback/AnimatedCheckmark/AnimatedCheckMark";
import Flx from "../layout/Flx";
import Htag from "../typography/Htag";
import Txt from "../typography/Txt";
import RffFormSpy from "./shared/RffFormSpy";

const RffFullPageFormStepper = ({
  onSubmit,
  children,
  initialValues,
  formSpy,
  loading,
  className,
  success,
  formDescription,
  formTitle,
  defaultStep = 0,
  hideLogo = false,
  customLogoName,
  successTitle = "Submission Received!",
  successDescription = "Thank you! We've received your submission and will review it shortly.",
  allowStepClickThrough = false,
  finalStepLabel = "Finish",
  sx,
}) => {
  const containerRef = useRef(null);
  const steps = React.Children.toArray(children).filter(Boolean);
  const [step, setStep] = useState(defaultStep || 0);
  const [values, setValues] = useState(initialValues || {});
  const totalSteps = children.length;
  const isLastStep = step === totalSteps - 1;
  const isMobile = useMediaQuery("(max-width:888px)");

  const [stepErrorMessage, setStepErrorMessage] = useState("");
  const [stepErrorFields, setStepErrorFields] = useState([]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step]);

  const handleNext = (formValues) => {
    setValues(formValues);
    setStepErrorMessage("");
    setStepErrorFields([]);
    setStep(Math.min(step + 1, totalSteps - 1));
  };

  const handleBack = () => {
    setStepErrorMessage("");
    setStepErrorFields([]);
    setStep(Math.max(step - 1, 0));
  };

  if (success) {
    return (
      <Box
        ref={containerRef}
        sx={{
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          height: "100%",
          width: "100%",
          py: 3,
          px: 1,
          position: "relative",
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            pt: 6,
            pb: 6,
            pl: 2,
            pr: 2,
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {hideLogo ? null : <EnvLogo width="105px" />}
          {customLogoName && <CustomLogoName name={customLogoName} />}

          <Card
            gap={6}
            sx={{
              p: 4,
            }}
          >
            <Flx gap={1.5} center column>
              <AnimatedCheckmark size="44px" />
              <Htag sx={{ mt: 2 }}>{successTitle}</Htag>
              <Txt center>{successDescription}</Txt>
            </Flx>
          </Card>
        </Container>
      </Box>
    );
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && step !== totalSteps - 1) {
      e.preventDefault();
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={values}
      validate={steps[step].props.validate}
      mutators={{ ...arrayMutators }}
    >
      {({ handleSubmit, form, submitting, pristine, values: formValues }) => {
        const currentStepValidate = steps[step].props.validate;
        const runStepValidation = (vals) =>
          (typeof currentStepValidate === "function" &&
            currentStepValidate(vals)) ||
          {};

        return (
          <Box
            component="form"
            onSubmit={handleSubmit}
            className={className}
            onKeyDown={handleKeyDown}
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              width: "100%",
              height: "100%",
              position: "relative",
              zIndex: 1,
              ...sx,
            }}
          >
            <FullPageFormStepperLayout
              isMobile={isMobile}
              sidebarWidth={275}
              hideLogo={hideLogo}
              customLogoName={customLogoName}
              mobileHeaderHeight="54px"
              mobileNavEndContent={
                <FormStepperProgress step={step} isMobile children={children} />
              }
              sidebar={
                <Box
                  sx={{
                    background: grey[100],
                    borderRight: `1px solid ${grey[300]}`,
                    height: "100%",
                    p: 2,
                  }}
                >
                  {hideLogo ? null : (
                    <Flx center sx={{ p: 3, pb: 4 }}>
                      {customLogoName ? (
                        <CustomLogoName name={customLogoName} />
                      ) : (
                        <EnvLogo />
                      )}
                    </Flx>
                  )}

                  <FormStepperProgress
                    sx={{ margin: "0 auto", pl: 2 }}
                    step={step}
                    children={children}
                    allowStepClickThrough={allowStepClickThrough}
                    onStepClick={async (targetStep) => {
                      if (targetStep <= step) {
                        setStepErrorMessage("");
                        setStepErrorFields([]);
                        setStep(targetStep);
                        return;
                      }
                      const stepErrors = runStepValidation(formValues);
                      if (Object.keys(stepErrors).length === 0) {
                        handleNext(formValues);
                      } else {
                        setStepErrorMessage(
                          "Some required fields are missing or invalid",
                        );
                        setStepErrorFields(Object.keys(stepErrors));
                      }
                    }}
                  />
                </Box>
              }
            >
              <Box
                ref={containerRef}
                sx={{
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                  height: "100%",
                  width: "100%",
                  position: "relative",
                }}
              >
                <StepperLinearProgress
                  sx={{ top: isMobile ? "54px" : 0 }}
                  step={step}
                  totalSteps={totalSteps}
                />
                <Container maxWidth="md">
                  <Flx
                    column
                    fw
                    sx={{
                      p: 2,
                      pt: isMobile ? 12 : 8,
                      pb: 12,
                      ".rff-group": {
                        mb: 8,
                      },
                    }}
                  >
                    {step === 0 && (
                      <FormTitleAndDescription
                        title={formTitle}
                        description={formDescription}
                      />
                    )}

                    {steps[step]}

                    {stepErrorFields.length > 0 && (
                      <Box mt={2}>
                        <Txt center color="error">
                          {stepErrorMessage}
                        </Txt>
                        <Flx
                          column
                          sx={{
                            textAlign: "left",
                            pl: 4,
                            mt: 1,
                            color: "error.main",
                          }}
                        >
                          {stepErrorFields.map((field) => {
                            let fieldLabel =
                              field === "uploadMetadata"
                                ? "File Upload"
                                : field;

                            return (
                              <li key={field}>
                                <Txt>{camelCaseToSentence(fieldLabel)}</Txt>
                              </li>
                            );
                          })}
                        </Flx>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 3,
                      }}
                    >
                      {step > 0 && (
                        <Button
                          onClick={handleBack}
                          sx={{ mr: 1 }}
                          size="large"
                          variant="outlined"
                          type="button"
                        >
                          Back
                        </Button>
                      )}

                      {!isLastStep ? (
                        <Button
                          size="large"
                          variant="contained"
                          endIcon={<ChevronRight />}
                          type="button"
                          onClick={() => {
                            form
                              .getRegisteredFields()
                              .forEach((name) => form.blur(name));

                            const { errors } = form.getState();
                            if (!errors || Object.keys(errors).length === 0) {
                              handleNext(formValues);
                            } else {
                              setStepErrorMessage(
                                "Please correct the errors above before continuing.",
                              );
                              setStepErrorFields(Object.keys(errors));
                            }
                          }}
                        >
                          Continue
                        </Button>
                      ) : (
                        <Button
                          loading={loading}
                          size="large"
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={
                            submitting || (pristine && step !== totalSteps - 1)
                          }
                        >
                          {finalStepLabel}
                        </Button>
                      )}
                    </Box>

                    <RffFormSpy formSpy={formSpy} />
                  </Flx>
                </Container>
              </Box>
            </FullPageFormStepperLayout>
          </Box>
        );
      }}
    </Form>
  );
};

export default RffFullPageFormStepper;

/* --------------------------------------- */
/*      Helper Components Below            */
/* --------------------------------------- */

const StepperLinearProgress = ({ step, totalSteps, sx }) => {
  if (step === 0) return null;

  return (
    <LinearProgress
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        height: "6px",
        width: "100%",
        ...sx,
      }}
      variant="determinate"
      value={(step / (totalSteps - 1)) * 100}
    />
  );
};

const FormTitleAndDescription = ({ title, description }) => {
  if (!title && !description) return null;

  return (
    <>
      <Flx column gap={1} sx={{ mt: 2, mb: 10 }}>
        {title && (
          <Htag
            h1
            sx={{
              fontFamily: "var(--inter)",
              fontSize: "36px",
              lineHeight: "54px",
            }}
          >
            {title}
          </Htag>
        )}
        {description && <Txt fontSize="18px">{description}</Txt>}
      </Flx>
      <Divider sx={{ mb: 6 }} />
    </>
  );
};

const FormStepperProgress = ({
  step,
  children,
  sx,
  onStepClick,
  allowStepClickThrough,
  isMobile,
}) => {
  if (isMobile) {
    return (
      <MobileStepper
        variant="dots"
        steps={children.length}
        position="static"
        activeStep={step}
        sx={{
          "&.MuiPaper-root": {
            p: 0,
            background: "none",
          },
          ...sx,
        }}
      />
    );
  }

  return (
    <Stepper orientation="vertical" activeStep={step} sx={sx} nonLinear>
      {children.map((child, index) => (
        <Step key={index}>
          {allowStepClickThrough ? (
            <StepButton onClick={() => onStepClick && onStepClick(index)}>
              {child.props.stepName}
            </StepButton>
          ) : (
            <StepLabel>{child.props.stepName}</StepLabel>
          )}
        </Step>
      ))}
    </Stepper>
  );
};

const FullPageFormStepperLayout = ({
  isMobile,
  sidebarWidth = 320,
  mobileHeaderHeight = "54px",
  mobileNavEndContent,
  hideLogo,
  customLogoName,
  sidebar,
  children,
}) => {
  if (isMobile) {
    return (
      <>
        <MobileNavbar
          hideLogo={hideLogo}
          customLogoName={customLogoName}
          mobileHeaderHeight={mobileHeaderHeight}
          mobileNavEndContent={mobileNavEndContent}
        />
        <Box sx={{ flexGrow: 1, display: "flex", width: "100%" }}>
          {children}
        </Box>
      </>
    );
  }

  return (
    <>
      <DesktopNavbar drawerWidth={sidebarWidth}>{sidebar}</DesktopNavbar>
      <Box sx={{ flexGrow: 1, display: "flex", width: "100%" }}>{children}</Box>
    </>
  );
};

const MobileNavbar = ({
  hideLogo,
  customLogoName,
  mobileNavEndContent,
  mobileHeaderHeight,
}) => {
  return (
    <Flx
      fw
      g={2}
      ac
      jb
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        height: mobileHeaderHeight,
        background: "#ffffff",
        pl: 2,
        pr: 3,
        flexShrink: 0,
        flexGrow: 0,
        zIndex: 222,
        width: "100%",
        boxShadow:
          "rgba(0, 0, 0, 0.32) 0px 0px 1px, rgba(0, 0, 0, 0.08) 0px 0px 2px, rgba(0, 0, 0, 0.08) 0px 1px 3px",
      }}
    >
      {hideLogo ? null : customLogoName ? (
        <CustomLogoName fontSize="16px" name={customLogoName} />
      ) : (
        <EnvLogo maxHeight={34} maxWidth={120} />
      )}

      {mobileNavEndContent}
    </Flx>
  );
};

const DesktopNavbar = ({ children, drawerWidth = 210, open = true }) => {
  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        background: "red",
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          position: "absolute",
          boxSizing: "border-box",
        },
      }}
      variant="persistent"
      anchor="left"
      open={open}
    >
      {children}
    </Drawer>
  );
};

const CustomLogoName = ({ fontSize = "24px", name }) => {
  if (isNil(name) || String(name).trim() === "") return null;

  return (
    <Txt
      sx={{
        fontSize,
        fontWeight: 700,
        color: "#2962ff",
        lineHeight: 1.2,
        textAlign: "center",
        wordBreak: "break-word",
      }}
    >
      {String(name).trim()}
    </Txt>
  );
};
